import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import Network, { IsBeta } from "../../../utils/Network";
import { RecentMatchesData } from "../../../types/global";
import ReactPaginate from "react-paginate";
import useWindowSize from "../../../hooks/useWindowSize";
import CircularProgress from "../../../components/CircularProgress";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import BattleRecordComponent from "./battle-record";
import {
  BattleFormationRefinedTroop,
  BattleFormationTroop,
  Troop,
} from "../../types";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

type Props = {};

const HistoryBattles = (props: Props) => {
  const { t } = useAppTranslation("battle");
  const network = new Network();
  const [isMine, setIsMine] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const {} = useWindowSize();
  const userInfo = useSelector((state: any) => state?.userInfo);
  const [matches, setMatches] = React.useState<RecentMatchesData["data"]>();
  const troopsRef = useRef<Troop[]>();
  const [pagination, setPagination] = React.useState({
    page: 1,
    total: 0,
  });

  const getRecentBattles = async (page: number = 1) => {
    setLoading(true);
    if (!troopsRef.current) {
      const response = await network.CallApi<Troop[]>({
        url: `/troop`,
        method: "GET",
      });
      troopsRef.current = response.data;
    }
    const res = await network.CallApi<RecentMatchesData>({
      url: `/api/battle/recent?page=${page}&count=20${
        !isMine ? "&playerId=0" : "&playerId=" + userInfo.id
      }`,
      customHost: `https://${IsBeta?"devbattle":"battle"}.chainoflegends.com`,
      method: "GET",
    });

    if (res.ok) {
      const data = res.data.data.map((match) => {
        const attackerFormation = match.formation
          ? JSON.parse(match.formation)
          : null;
        const defenderFormation = match.defendFormation
          ? JSON.parse(match.defendFormation)
          : null;
        const isThereWinner = match.players.find((player) => player.isWinner);
        return {
          ...match,
          defendFormation: undefined,
          formation: undefined,
          isThereWinner: Boolean(isThereWinner),
          attackerTroops: attackerFormation
            ? groupAndCountTroops(attackerFormation)
            : undefined,
          defenderTroops: defenderFormation
            ? groupAndCountTroops(defenderFormation)
            : undefined,
        };
      });
      setMatches(data);
      setPagination({ total: Math.ceil(res.data?.totalCount / 20), page });
    }
    setLoading(false);
  };

  useEffect(() => {
    getRecentBattles();
  }, [isMine]);

  const groupAndCountTroops = useCallback(
    (data: BattleFormationTroop[]) => {
      const groupedTroops = {};

      data.forEach((troop) => {
        const key = `${troop.TroopId}-${troop.Level}`;
        const additionalTroopData = troopsRef.current?.find(
          (stateTroop) => stateTroop.id === troop.TroopId
        );
        if (!groupedTroops[key]) {
          groupedTroops[key] = {
            ...additionalTroopData, // Merge stats from troops state
            troopId: troop.TroopId,
            level: troop.Level,
            count: 0,
            stats: {
              Health: troop.Health,
              MaxHealth: troop.MaxHealth,
              Damage: troop.Damage,
              Armor: troop.Armor,
              MagicResistance: troop.MagicResistance,
            },
            originalStats: troop.OriginalStats,
            positions: [], // Store positions of each troop in this group
            abilities: troop.Abilities,
            buffs: troop.Buffs,
            isInField: troop.isInField,
            targetPriority: troop.targetPriority,
          };
        }
        groupedTroops[key].count += 1;
        groupedTroops[key].positions.push({ X: troop.X, Y: troop.Y });
      });

      // Convert the grouped object into an array for easier manipulation
      return Object.values(groupedTroops) as BattleFormationRefinedTroop[];
    },
    [troopsRef.current] // Ensure the function is updated when troops state changes
  );

  return (
    <div className=" px-0 px-md-3 ">
      <div className="pb-2 d-flex fs-med justify-content-center">
        <Form.Check
          type="checkbox"
          label={t("history.showMyBattles")}
          checked={isMine}
          onClick={() => setIsMine((prev) => !prev)}
        />
      </div>
      {loading && (
        <div className="py-5 my-5 d-flex justify-content-center align-items-center">
          <CircularProgress />
        </div>
      )}
      <div>
        {!loading &&
          matches?.map((match, index) => {
            return <BattleRecordComponent match={match} key={index} />;
          })}
      </div>

      <ReactPaginate
        className="pagination mb-0 justify-content-center"
        activeClassName="active-pg"
        pageClassName="page-item"
        pageLinkClassName="page-link"
        breakClassName="page-item"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        breakLinkClassName="page-link"
        previousLinkClassName="page-link"
        previousClassName="page-item"
        previousLabel="<"
        nextLabel=">"
        breakLabel="..."
        onPageChange={(e) => getRecentBattles(e.selected + 1)}
        pageRangeDisplayed={3}
        pageCount={pagination.total}
        // renderOnZeroPageCount={null}
      />
    </div>
  );
};

export default HistoryBattles;
